import { useState, useEffect, useCallback, Fragment, useMemo } from 'react';
import { sortByName } from '../lib/sortByName';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../lib/auth';
import { auditAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
    Shield, Search, RefreshCw, UserCheck, UserX, LogIn, LogOut, Key, CreditCard,
    AlertTriangle, Download, Filter, ChevronLeft, ChevronRight, Clock, Users,
    Activity, Globe, Settings, Eye, MonitorSmartphone, ChevronDown, ChevronUp,
} from 'lucide-react';
import { parseUtc } from '../lib/datetime';
import { buildDispatchAnchorUrl } from '../lib/audit_anchor';
import { WhatTheyDidPanel } from '../components/audit/WhatTheyDidPanel';
import { ChatAuditPanel } from '../components/audit/ChatAuditPanel';

const ACTION_CONFIG = {
    login_success: { icon: LogIn, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', label: 'Login' },
    login_failed: { icon: UserX, color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'Failed Login' },
    logout: { icon: LogOut, color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', label: 'Logout' },
    session_expired: { icon: AlertTriangle, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', label: 'Session Expired' },
    session_kicked: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', label: 'Session Kicked' },
    password_change: { icon: Key, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', label: 'Password Change' },
    user_create: { icon: UserCheck, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', label: 'User Created' },
    user_update: { icon: UserCheck, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', label: 'User Updated' },
    user_delete: { icon: UserX, color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'User Deleted' },
    role_change: { icon: Shield, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', label: 'Role Changed' },
    card_create: { icon: CreditCard, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', label: 'Card Created' },
    card_update: { icon: CreditCard, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', label: 'Card Updated' },
    card_delete: { icon: CreditCard, color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'Card Deleted' },
    export_full: { icon: Download, color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', label: 'Full Export' },
    export_summary: { icon: Download, color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300', label: 'Summary Export' },
    export_denied: { icon: AlertTriangle, color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'Export Denied' },
    permission_denied: { icon: AlertTriangle, color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: 'Permission Denied' },
    permission_change: { icon: Shield, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', label: 'Permission Change' },
    settings_change: { icon: Settings, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', label: 'Settings Change' },
    // P_USER_WELCOME_DISPATCH_3.1 — Communications Hub audit categories.
    welcome_dispatch_created: { icon: Eye, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', label: 'Welcome Dispatch' },
    campaign_dispatch_created: { icon: Eye, color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', label: 'Campaign Dispatch' },
};

const getActionConfig = (action) =>
    ACTION_CONFIG[action] || { icon: Shield, color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', label: action };

// v4.34.12 — Expandable detail row helpers (Operational Denial
// Visibility). Kept compact + dark/light compatible.
function DetailBlock({ title, children }) {
    return (
        <div className="rounded border border-border bg-card/40 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</div>
            <div className="space-y-1">{children}</div>
        </div>
    );
}

function DetailRow({ label, value, mono = false, truncate = false }) {
    const shown = value === undefined || value === null || value === '' ? '—' : String(value);
    return (
        <div className="grid grid-cols-[140px_1fr] gap-2 text-xs">
            <div className="text-muted-foreground">{label}</div>
            <div className={`${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : 'break-all'}`} title={shown}>
                {shown}
            </div>
        </div>
    );
}

export default function AuditLogs() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isSuperAdmin, isPartner } = useAuth();
    const [stepsFor, setStepsFor] = useState(null);   // { session_id, email } for the side window

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [activeSessions, setActiveSessions] = useState([]);
    const [organizations, setOrganizations] = useState([]);

    // Filters
    const [actionFilter, setActionFilter] = useState('all');
    const [orgFilter, setOrgFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // v4.34.12 — Denial Visibility filters
    const [deniedOnly, setDeniedOnly] = useState(false);
    const [denialCategoryFilter, setDenialCategoryFilter] = useState('all');
    const [denialReasonFilter, setDenialReasonFilter] = useState('all');

    // v4.34.12 — Expanded row state (key = row index per page)
    const [expanded, setExpanded] = useState({});

    // Pagination
    const [page, setPage] = useState(0);
    const limit = 50;

    // Summary stats
    const [stats, setStats] = useState({ total: 0, logins: 0, failed: 0, active: 0 });

    useEffect(() => {
        if (!isSuperAdmin()) { navigate('/'); return; }
        loadOrgs();
        loadSessions();
    }, []);

    useEffect(() => { fetchLogs(); }, [page, actionFilter, orgFilter, roleFilter, startDate, endDate]);

    const loadOrgs = async () => {
        try {
            const res = await api.get('/organizations');
            setOrganizations(sortByName(res.data));
        } catch {}
    };

    const loadSessions = async () => {
        try {
            const res = await auditAPI.getActiveSessions();
            setActiveSessions(res.data.sessions || []);
        } catch {}
    };

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit, skip: page * limit };
            if (actionFilter !== 'all') params.action = actionFilter;
            if (orgFilter !== 'all') params.organization_id = orgFilter;
            if (roleFilter !== 'all') params.role = roleFilter;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await auditAPI.getLogs(params);
            const allLogs = response.data.logs || [];
            setLogs(allLogs);
            setTotal(response.data.total || 0);

            // Compute summary stats from first page
            if (page === 0) {
                const logins = allLogs.filter(l => l.action === 'login_success').length;
                const failed = allLogs.filter(l => l.action === 'login_failed').length;
                setStats({ total: response.data.total, logins, failed, active: activeSessions.length });
            }
        } catch (err) {
            toast.error('Failed to load audit logs');
        } finally { setLoading(false); }
    }, [page, actionFilter, orgFilter, roleFilter, startDate, endDate, activeSessions.length]);

    const handleExportCsv = async () => {
        try {
            const params = { format: 'csv' };
            if (actionFilter !== 'all') params.action = actionFilter;
            if (orgFilter !== 'all') params.organization_id = orgFilter;
            if (roleFilter !== 'all') params.role = roleFilter;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const res = await auditAPI.exportCsv(params);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Audit logs exported');
        } catch { toast.error('Failed to export'); }
    };

    const filteredLogs = logs.filter(log => {
        // v4.34.12 — Denial Visibility filters
        if (deniedOnly && log.action !== 'permission_denied') return false;
        if (denialCategoryFilter !== 'all' && log.details?.denial_category !== denialCategoryFilter) return false;
        if (denialReasonFilter !== 'all' && log.details?.denial_reason !== denialReasonFilter) return false;

        if (!searchTerm) return true;
        const t = searchTerm.toLowerCase();
        const det = log.details || {};
        return (
            log.user_email?.toLowerCase().includes(t) ||
            log.action?.toLowerCase().includes(t) ||
            log.ip_address?.toLowerCase().includes(t) ||
            log.user_role?.toLowerCase().includes(t) ||
            // v4.34.12 — extend search into denial route/category/reason
            det.attempted_route?.toLowerCase?.().includes(t) ||
            det.denial_category?.toLowerCase?.().includes(t) ||
            det.denial_reason?.toLowerCase?.().includes(t)
        );
    });

    // v4.34.12 — Helper: identify expandable rows (denials + session
    // end events carry rich structured details under `log.details`).
    const isExpandable = (log) => {
        if (!log || typeof log.details !== 'object' || !log.details) return false;
        return ['permission_denied', 'logout', 'session_kicked', 'session_expired'].includes(log.action);
    };

    // v4.34.12 — Format a duration in human-friendly form.
    const fmtDuration = (s) => {
        if (s == null || s === '') return '—';
        const n = Number(s);
        if (Number.isNaN(n) || n < 0) return '—';
        if (n < 60) return `${n}s`;
        if (n < 3600) return `${Math.floor(n / 60)}m ${n % 60}s`;
        return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
    };

    const formatDate = (ts) => {
        try { return format(parseUtc(ts), 'MMM d, yyyy HH:mm:ss'); } catch { return ts || ''; }
    };
    const formatRelative = (ts) => {
        try { return formatDistanceToNow(parseUtc(ts), { addSuffix: true }); } catch { return ''; }
    };

    const totalPages = Math.ceil(total / limit);
    const actions = auditAPI.getActions();

    return (
        <div className="space-y-6" data-testid="audit-logs-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-red-500" />
                    <div>
                        <h1 className="text-2xl font-bold">Activity & Audit Log</h1>
                        <p className="text-sm text-muted-foreground">Complete activity tracking and security audit trail</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { fetchLogs(); loadSessions(); }} data-testid="audit-refresh-btn" data-insight-id="audit.action.refresh" data-insight-label="Refresh audit log">
                        <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="audit-export-btn" data-insight-id="audit.workflow.export-csv" data-insight-label="Export audit log as CSV">
                        <Download className="w-4 h-4 mr-1" /> Export CSV
                    </Button>
                </div>
            </div>

            {/* Summary Cards — matches Review Shield design */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card data-insight-id="audit.stat.total-events" data-insight-label="Total events">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                            <Activity className="w-4 h-4" /> Total Events
                        </div>
                        <div className="text-2xl font-bold text-foreground" data-testid="audit-stat-total">{total}</div>
                    </CardContent>
                </Card>
                <Card data-insight-id="audit.stat.logins" data-insight-label="Successful logins">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                            <LogIn className="w-4 h-4 text-emerald-500" /> Successful Logins
                        </div>
                        <div className="text-2xl font-bold text-emerald-600" data-testid="audit-stat-logins">{stats.logins}</div>
                    </CardContent>
                </Card>
                <Card data-insight-id="audit.stat.failed" data-insight-label="Failed logins">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                            <AlertTriangle className="w-4 h-4 text-red-500" /> Failed Logins
                        </div>
                        <div className="text-2xl font-bold text-red-600" data-testid="audit-stat-failed">{stats.failed}</div>
                    </CardContent>
                </Card>
                <Card data-insight-id="audit.stat.active-sessions" data-insight-label="Active sessions">
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                            <Users className="w-4 h-4 text-blue-500" /> Active Sessions
                        </div>
                        <div className="text-2xl font-bold text-blue-600" data-testid="audit-stat-active">{activeSessions.length}</div>
                    </CardContent>
                </Card>
            </div>

            {/* Active Sessions Panel */}
            {activeSessions.length > 0 && (
                <Card data-testid="active-sessions-panel" data-insight-id="audit.workflow.active-sessions-panel" data-insight-label="Currently active sessions (read-only)">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <MonitorSmartphone className="w-4 h-4 text-emerald-500" />
                            Currently Active Sessions
                            <Badge variant="secondary" className="ml-1">{activeSessions.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-2">
                            {activeSessions.map((s) => (
                                <div key={s.id || s.session_id || `${s.user_email}-${s.created_at}`} className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/40 text-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                        <div>
                                            <span className="font-medium">{s.user_name || s.user_email}</span>
                                            <Badge variant="outline" className="ml-2 text-xs">{s.user_role}</Badge>
                                            {s.organization_name && <span className="text-muted-foreground ml-2">{s.organization_name}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{s.ip_address}</span>
                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatRelative(s.created_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Chat & messages — the player chat's own 7-day record. */}
            <ChatAuditPanel organizationId={orgFilter} />

            {/* Filters — matches Review Shield layout */}            <Card>
                <CardContent className="pt-4">
                    <div className="flex flex-wrap gap-3 items-end">
                        <div className="flex-1 min-w-[200px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by user, action, IP..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9"
                                    data-testid="audit-search"
                                    data-insight-id="audit.workflow.search"
                                    data-insight-label="Audit log search"
                                />
                            </div>
                        </div>
                        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
                            <SelectTrigger className="w-[160px]" data-testid="audit-filter-action" data-insight-id="audit.workflow.filter-action" data-insight-label="Filter by action class">
                                <Filter className="w-3.5 h-3.5 mr-1" />
                                <SelectValue placeholder={t('auditLogs.action')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Actions</SelectItem>
                                {actions.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v); setPage(0); }}>
                            <SelectTrigger className="w-[160px]" data-testid="audit-filter-org">
                                <SelectValue placeholder={t('locationHealth.organization')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Organizations</SelectItem>
                                {organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(0); }}>
                            <SelectTrigger className="w-[140px]" data-testid="audit-filter-role" data-insight-id="audit.workflow.filter-role" data-insight-label="Filter by operator role">
                                <SelectValue placeholder={t('auditLogs.role')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Roles</SelectItem>
                                <SelectItem value="super_admin">Super Admin</SelectItem>
                                <SelectItem value="franchise_admin">Franchise Admin</SelectItem>
                                <SelectItem value="client_admin">Client Admin</SelectItem>
                                <SelectItem value="location_manager">Location Manager</SelectItem>
                                <SelectItem value="employee">Employee</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(0); }} className="w-[140px]" data-testid="audit-filter-start" data-insight-id="audit.workflow.date-scope" data-insight-label="Audit date scope (start)" />
                        <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(0); }} className="w-[140px]" data-testid="audit-filter-end" data-insight-id="audit.workflow.date-scope" data-insight-label="Audit date scope (end)" />
                    </div>

                    {/* v4.34.12 — Denial Visibility filter row */}
                    <div className="flex flex-wrap gap-2 items-center mt-3 pt-3 border-t">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider mr-1">
                            {t('auditLogs.denial.filtersLabel')}
                        </span>
                        <Button
                            type="button"
                            size="sm"
                            variant={deniedOnly ? 'default' : 'outline'}
                            onClick={() => { setDeniedOnly(!deniedOnly); }}
                            className="h-8"
                            data-testid="audit-filter-denied-only"
                        >
                            <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                            {t('auditLogs.denial.deniedOnly')}
                        </Button>
                        <Select value={denialCategoryFilter} onValueChange={setDenialCategoryFilter}>
                            <SelectTrigger className="w-[200px] h-8" data-testid="audit-filter-denial-category">
                                <SelectValue placeholder={t('auditLogs.denial.categoryAll')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('auditLogs.denial.categoryAll')}</SelectItem>
                                <SelectItem value="route_access_denied">{t('auditLogs.denial.category.route_access_denied')}</SelectItem>
                                <SelectItem value="api_permission_denied">{t('auditLogs.denial.category.api_permission_denied')}</SelectItem>
                                <SelectItem value="cross_org_access_denied">{t('auditLogs.denial.category.cross_org_access_denied')}</SelectItem>
                                <SelectItem value="expired_session_access">{t('auditLogs.denial.category.expired_session_access')}</SelectItem>
                                <SelectItem value="super_admin_only_denied">{t('auditLogs.denial.category.super_admin_only_denied')}</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={denialReasonFilter} onValueChange={setDenialReasonFilter}>
                            <SelectTrigger className="w-[200px] h-8" data-testid="audit-filter-denial-reason">
                                <SelectValue placeholder={t('auditLogs.denial.reasonAll')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('auditLogs.denial.reasonAll')}</SelectItem>
                                <SelectItem value="insufficient_role">{t('auditLogs.denial.reason.insufficient_role')}</SelectItem>
                                <SelectItem value="invalid_org_scope">{t('auditLogs.denial.reason.invalid_org_scope')}</SelectItem>
                                <SelectItem value="invalid_location_scope">{t('auditLogs.denial.reason.invalid_location_scope')}</SelectItem>
                                <SelectItem value="expired_token">{t('auditLogs.denial.reason.expired_token')}</SelectItem>
                                <SelectItem value="feature_not_enabled">{t('auditLogs.denial.reason.feature_not_enabled')}</SelectItem>
                                <SelectItem value="unauthorized_resource_access">{t('auditLogs.denial.reason.unauthorized_resource_access')}</SelectItem>
                            </SelectContent>
                        </Select>
                        {(deniedOnly || denialCategoryFilter !== 'all' || denialReasonFilter !== 'all') && (
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-8"
                                onClick={() => { setDeniedOnly(false); setDenialCategoryFilter('all'); setDenialReasonFilter('all'); }}
                                data-testid="audit-filter-denial-clear"
                            >
                                {t('auditLogs.denial.clearFilters')}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Audit Log Table */}
            <Card>
                <CardContent className="pt-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : filteredLogs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Shield className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p>{t('auditLogs.noEventsFound')}</p>
                        </div>
                    ) : (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[40px]"></TableHead>
                                        <TableHead className="w-[180px]">Timestamp</TableHead>
                                        <TableHead>Action</TableHead>
                                        <TableHead>User</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>IP Address</TableHead>
                                        <TableHead>Details</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredLogs.map((log, idx) => {
                                        const config = getActionConfig(log.action);
                                        const Icon = config.icon;
                                        const rowKey = log.id || `${log.timestamp}-${idx}`;
                                        const canExpand = isExpandable(log);
                                        const isOpen = !!expanded[rowKey];
                                        const det = log.details || {};
                                        const isDenial = log.action === 'permission_denied';
                                        return (
                                            <Fragment key={rowKey}>
                                                <TableRow
                                                    data-testid={`audit-row-${idx}`}
                                                    data-insight-id={idx === 0 ? "audit.workflow.row" : undefined}
                                                    data-insight-label={idx === 0 ? "Audit record row" : undefined}
                                                    className={canExpand ? 'cursor-pointer hover:bg-muted/40' : ''}
                                                    onClick={canExpand ? () => setExpanded({ ...expanded, [rowKey]: !isOpen }) : undefined}
                                                >
                                                    <TableCell className="py-2">
                                                        {canExpand ? (
                                                            isOpen
                                                                ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                                                : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell className="text-xs whitespace-nowrap">
                                                        <div>{formatDate(log.timestamp)}</div>
                                                        <div className="text-muted-foreground">{formatRelative(log.timestamp)}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={`${config.color} gap-1`} variant="secondary">
                                                            <Icon className="w-3 h-3" />
                                                            {config.label}
                                                        </Badge>
                                                        {isDenial && det.denial_category && (
                                                            <div className="mt-1">
                                                                <Badge variant="outline" className="text-[10px] font-normal" data-testid={`audit-row-${idx}-category`}>
                                                                    {t(`auditLogs.denial.category.${det.denial_category}`, det.denial_category)}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-sm">
                                                        <div>{log.user_email || '-'}</div>
                                                        {!isPartner() && log.action === 'login_success' && det.session_id && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setStepsFor({
                                                                    session_id: det.session_id,
                                                                    user_id: log.user_id || null,
                                                                    email: log.user_email,
                                                                }); }}
                                                                className="mt-0.5 inline-flex items-center gap-1 text-primary hover:underline text-xs font-medium"
                                                                data-testid={`audit-view-what-they-did-${idx}`}
                                                            >
                                                                <Activity className="w-3 h-3" />
                                                                {t('auditLogs.viewWhatTheyDid', 'View what they did')}
                                                            </button>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className="text-xs">{log.user_role || '-'}</Badge>
                                                    </TableCell>
                                                    <TableCell className="text-xs font-mono text-muted-foreground">{log.ip_address || '-'}</TableCell>
                                                    <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">
                                                        {isDenial ? (
                                                            <span>
                                                                {det.request_method && <span className="font-mono mr-1">{det.request_method}</span>}
                                                                <span className="font-mono">{det.attempted_route || ''}</span>
                                                            </span>
                                                        ) : log.action === 'logout' ? (
                                                            <span>
                                                                {t('auditLogs.denial.session.duration')}: {fmtDuration(det.session_duration_seconds)}
                                                                {det.termination_reason && <span className="ml-1">· {t(`auditLogs.denial.termination.${det.termination_reason}`, det.termination_reason)}</span>}
                                                            </span>
                                                        ) : buildDispatchAnchorUrl(det) ? (
                                                            <a
                                                                href={buildDispatchAnchorUrl(det)}
                                                                onClick={(e) => { e.stopPropagation(); }}
                                                                className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                                                                data-testid={`audit-view-user-saw-${idx}`}>
                                                                <Eye className="w-3 h-3" />
                                                                {t('auditLogs.viewUserSaw')}
                                                            </a>
                                                        ) : (
                                                            typeof log.details === 'object' ? JSON.stringify(log.details) : (log.details || '-')
                                                        )}
                                                    </TableCell>
                                                </TableRow>

                                                {canExpand && isOpen && (
                                                    <TableRow data-testid={`audit-row-${idx}-expanded`}>
                                                        <TableCell colSpan={7} className="bg-muted/30 p-0">
                                                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                                                {isDenial && (
                                                                    <>
                                                                        <DetailBlock title={t('auditLogs.denial.section.what')}>
                                                                            <DetailRow label={t('auditLogs.denial.field.attemptedRoute')} value={det.attempted_route} mono />
                                                                            <DetailRow label={t('auditLogs.denial.field.attemptedAction')} value={det.attempted_action} mono />
                                                                            <DetailRow label={t('auditLogs.denial.field.requestMethod')} value={det.request_method} mono />
                                                                        </DetailBlock>
                                                                        <DetailBlock title={t('auditLogs.denial.section.why')}>
                                                                            <DetailRow label={t('auditLogs.denial.field.category')} value={t(`auditLogs.denial.category.${det.denial_category}`, det.denial_category)} />
                                                                            <DetailRow label={t('auditLogs.denial.field.reason')} value={t(`auditLogs.denial.reason.${det.denial_reason}`, det.denial_reason)} />
                                                                            <DetailRow label={t('auditLogs.denial.field.requiredRoles')} value={(det.required_roles || []).join(', ') || '—'} />
                                                                            <DetailRow label={t('auditLogs.denial.field.userRoles')} value={(det.user_roles || []).join(', ') || '—'} />
                                                                        </DetailBlock>
                                                                        <DetailBlock title={t('auditLogs.denial.section.who')}>
                                                                            <DetailRow label={t('auditLogs.denial.field.userId')} value={det.user_id} mono />
                                                                            <DetailRow label={t('auditLogs.denial.field.userEmail')} value={det.user_email} />
                                                                            <DetailRow label={t('auditLogs.denial.field.organization')} value={det.organization_id} mono />
                                                                            <DetailRow label={t('auditLogs.denial.field.location')} value={det.location_id} mono />
                                                                        </DetailBlock>
                                                                        <DetailBlock title={t('auditLogs.denial.section.context')}>
                                                                            <DetailRow label={t('auditLogs.denial.field.targetResource')} value={det.target_resource_type ? `${det.target_resource_type}/${det.target_resource_id || '—'}` : '—'} mono />
                                                                            <DetailRow label={t('auditLogs.denial.field.userAgent')} value={log.user_agent} truncate />
                                                                            <DetailRow label={t('auditLogs.denial.field.ipAddress')} value={log.ip_address} mono />
                                                                            <DetailRow label={t('auditLogs.denial.field.timestamp')} value={formatDate(log.timestamp)} />
                                                                        </DetailBlock>
                                                                    </>
                                                                )}
                                                                {!isDenial && (
                                                                    <DetailBlock title={t('auditLogs.denial.section.session')}>
                                                                        <DetailRow label={t('auditLogs.denial.field.sessionId')} value={det.session_id} mono />
                                                                        <DetailRow label={t('auditLogs.denial.field.loginAt')} value={det.login_at ? formatDate(det.login_at) : '—'} />
                                                                        <DetailRow label={t('auditLogs.denial.field.logoutAt')} value={det.logout_at ? formatDate(det.logout_at) : '—'} />
                                                                        <DetailRow label={t('auditLogs.denial.session.duration')} value={fmtDuration(det.session_duration_seconds)} />
                                                                        <DetailRow label={t('auditLogs.denial.field.terminationReason')} value={det.termination_reason ? t(`auditLogs.denial.termination.${det.termination_reason}`, det.termination_reason) : '—'} />
                                                                        <DetailRow label={t('auditLogs.denial.field.userAgent')} value={log.user_agent} truncate />
                                                                    </DetailBlock>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            {/* Pagination — matches Review Shield */}
                            <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <p className="text-sm text-muted-foreground">
                                    Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
                                </p>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0} data-testid="audit-page-prev">
                                        <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <span className="flex items-center px-3 text-sm">{page + 1} / {totalPages || 1}</span>
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} data-testid="audit-page-next">
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* View what they did — the ordered steps of one sign-in. */}
            <WhatTheyDidPanel target={stepsFor} onClose={() => setStepsFor(null)} />
        </div>
    );
}
